import { useEffect, useRef, useState } from 'react';
import { Button, TextButton } from '@safetyhub/ui';
import { useQueryClient } from '@tanstack/react-query';
import Modal from '../Modal';
import { ParticipantSelector } from '../inspection/ParticipantSelector';
import { MemberSelect } from '../MemberSelect';
import { EditModalFooter } from '../common/EditModalFooter';
import { useDirtySnapshot } from '../../hooks/useDirtySnapshot';
import { useParticipantsState } from '../../hooks/useParticipantsState';
import { useToastStore } from '../../stores/toast';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { extractApiError } from '../../lib/api';
import { ChevronLeftIcon } from '../icons';
import {
  InspectionCollaborator,
  updateInspectionParticipants,
} from '../../lib/inspections';
import { organizationsApi, sitesApi } from '../../lib/organizations';

interface ParticipantsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  inspectionId: string;
  companyId: string;
  siteId: string;
  /** 현재 점검 참여자 목록 */
  currentParticipants: InspectionCollaborator[];
  /** 현재 점검의 inspectorId (PRIMARY fallback) */
  currentInspectorId?: string | null;
  /** 편집 가능 여부 (권한) */
  canEdit: boolean;
  /** 저장 성공 콜백 */
  onSuccess?: (collaborators: InspectionCollaborator[]) => void;
}

/**
 * 푸터 — 취소가 base Modal의 requestClose로 수렴해야 미저장 닫기 확인을 탄다.
 * context는 Modal children/footer의 **하위 컴포넌트**에서만 잡히므로 분리했다(base Modal 규약 —
 * 부모 본문에서 훅을 불러 `footer` prop에 JSX로 끼워 넣으면 null이다).
 *
 * 변경 표시는 편집 권한이 있을 때만 — 조회 전용으로 열린 사용자에게 "변경사항 없음"은
 * 알려줄 게 없는 문구다(저장 버튼도 이미 권한으로 잠겨 있다).
 */
function ParticipantsFooter({
  canEdit,
  isDirty,
  isSaving,
  isSaveDisabled,
  onSave,
  onFallbackClose,
}: {
  canEdit: boolean;
  isDirty: boolean;
  isSaving: boolean;
  isSaveDisabled: boolean;
  onSave: () => void;
  onFallbackClose: () => void;
}) {
  return (
    <EditModalFooter
      isDirty={isDirty}
      showHint={canEdit}
      submitLabel="저장"
      submitDisabled={isSaveDisabled}
      busy={isSaving}
      onSubmit={onSave}
      onFallbackClose={onFallbackClose}
    />
  );
}

/**
 * 진행 중 점검의 참가자 편집 모달.
 * - CreateInspectionModal과 동일한 참가자 선택 UI 재사용
 * - 저장 시 PUT /api/v1/inspections/:id/participants 호출
 */
export default function ParticipantsEditModal({
  isOpen,
  onClose,
  inspectionId,
  companyId,
  siteId,
  currentParticipants,
  currentInspectorId,
  canEdit,
  onSuccess,
}: ParticipantsEditModalProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const layout = useDeviceLayout();
  const isMobile = layout !== 'desktop';

  const [assignMode, setAssignMode] = useState<'list' | 'addMember' | 'inviteExternal'>('list');
  const [addMemberId, setAddMemberId] = useState('');
  const [inviteIdentifier, setInviteIdentifier] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const participantsHook = useParticipantsState({
    companyId,
    siteId,
    initialSource: {
      kind: 'explicit',
      participants: currentParticipants,
      inspectorId: currentInspectorId ?? undefined,
    },
    enabled: isOpen,
  });

  const { participants, unassignedMembers, members, validationError, checkedCount } = participantsHook;

  /**
   * 무변경 저장 차단 — 저장 payload(체크된 참가자 + 주담당자)가 기준점과 같으면 막는다.
   * 화면에 보이는 체크 상태가 곧 저장 대상이라 스냅샷도 payload로 잡는다
   * (결정 기록 `2026-08-16-edit-modal-dirty-gate-standard.md`).
   */
  const dirty = useDirtySnapshot(() => participantsHook.getPayload());
  const { capture: captureDirtyBaseline, reset: resetDirtyBaseline } = dirty;

  /**
   * 기준점은 **참가자 목록이 만들어진 뒤**의 payload다. 초기 목록은 현장 배정·활성 여부로
   * 걸러지고 주담당자 fallback까지 적용돼 넘겨받은 `currentParticipants`와 다를 수 있어,
   * prop을 기준으로 삼으면 열자마자 변경으로 잡힌다.
   *
   * 빌드 완료 신호는 `participantsHook.isInitialized`다 — participants 배열의 **참조 변화**에
   * 기대지 않는다(목록이 메모이즈되거나 빌드 후 다시 안 바뀌면 기준점을 영영 못 잡아 저장이
   * 조용히 영구 비활성이 되던 취약점을 없앤다). isInitialized가 true가 되는 커밋에서 딱 한 번 잡고,
   * 닫히면 리셋한다.
   */
  const baselineCapturedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      baselineCapturedRef.current = false;
      resetDirtyBaseline();
      return;
    }
    if (!baselineCapturedRef.current && participantsHook.isInitialized) {
      baselineCapturedRef.current = true;
      captureDirtyBaseline();
    }
  }, [isOpen, participantsHook.isInitialized, captureDirtyBaseline, resetDirtyBaseline]);

  const handleClose = () => {
    if (isSaving) return;
    setAssignMode('list');
    setAddMemberId('');
    setInviteIdentifier('');
    setInvitePhone('');
    onClose();
  };

  const handleAddMemberToSite = async () => {
    if (!addMemberId) return;
    setIsAddingMember(true);
    try {
      await sitesApi.assignMember(companyId, siteId, { memberId: addMemberId });
      await participantsHook.refetchSite();

      const addedMember = members.find((m) => m.id === addMemberId);
      if (addedMember) {
        participantsHook.addParticipant(addedMember);
      }

      setAssignMode('list');
      setAddMemberId('');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleInviteExternal = async () => {
    if (!inviteIdentifier.trim() && !invitePhone.trim()) {
      showToast('이메일/아이디 또는 전화번호를 입력해주세요.', 'error');
      return;
    }
    setIsInviting(true);
    try {
      const result = await organizationsApi.inviteMember(companyId, {
        identifier: inviteIdentifier.trim() || undefined,
        phone: invitePhone.trim() || undefined,
        // P7-b-2: 초대의 역할 축은 roleKeys 하나다. 이 자리는 「현장 참가자로 합류」라 역할을 고르는 UI가
        // 없으므로 비워 서버 기본값(`member`)을 받는다 — 옛 `role: 'SAFETY_MANAGER'` 하드코딩의 대체.
        siteId,
      });

      if (result.type === 'MEMBER_ADDED' && result.member) {
        await participantsHook.refetchSite();
        participantsHook.addParticipant(result.member);
      } else {
        showToast('초대가 발송되었습니다.', 'success');
      }

      setAssignMode('list');
      setInviteIdentifier('');
      setInvitePhone('');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsInviting(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) {
      showToast('편집 권한이 없습니다.', 'error');
      return;
    }
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }
    // 버튼 disabled를 우회한 경로까지 무변경 저장을 막는다.
    if (!dirty.isDirty) return;

    const payload = participantsHook.getPayload();
    const primaryUserId = payload.primaryParticipantUserId;
    if (!primaryUserId) {
      showToast('주담당자를 지정해주세요.', 'error');
      return;
    }

    const body = {
      participants: payload.participantUserIds.map((userId) => ({
        userId,
        role: (userId === primaryUserId ? 'PRIMARY' : 'SECONDARY') as 'PRIMARY' | 'SECONDARY',
      })),
    };

    setIsSaving(true);
    try {
      const response = await updateInspectionParticipants(inspectionId, companyId, body);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || '담당자 저장에 실패했습니다.');
      }

      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId, 'contributors'] });

      showToast('점검 담당자가 저장되었습니다.', 'success');
      onSuccess?.(response.data.collaborators);
      handleClose();
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const isSaveDisabled =
    isSaving || !canEdit || checkedCount === 0 || !!validationError || !dirty.isDirty;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="점검 담당자"
      description={isMobile ? undefined : '이 점검의 담당자를 선택하고 주담당자를 지정합니다.'}
      size={isMobile ? 'md' : 'xl'}
      preventClose={isSaving}
      isDirty={dirty.isDirty}
      footer={
        <ParticipantsFooter
          canEdit={canEdit}
          isDirty={dirty.isDirty}
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
          onSave={handleSave}
          onFallbackClose={handleClose}
        />
      }
    >
      {!canEdit && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-card text-sm text-amber-800">
          보고서가 생성되거나 점검이 완료되어 참가자를 편집할 수 없습니다. 현재 참가자 목록만 조회할 수 있습니다.
        </div>
      )}

      <div className={isMobile ? 'mb-4' : 'mb-6'}>
        <label className="block text-sm font-medium text-ink-700 mb-2">
          참가자 <span className="text-red-500">*</span>
        </label>
        {assignMode === 'list' ? (
          <ParticipantSelector
            participants={participants}
            onToggle={participantsHook.toggleMember}
            onSetPrimary={participantsHook.setPrimary}
            disabled={isSaving || !canEdit}
            onAddMember={canEdit ? () => setAssignMode('addMember') : undefined}
            emptyMessage="이 현장에 배정된 안전관리자가 없습니다."
          />
        ) : assignMode === 'addMember' ? (
          <div className="border border-ink-200 rounded-card p-3">
            <TextButton
              tone="neutral"
              onClick={() => { setAssignMode('list'); setAddMemberId(''); }}
              className="flex items-center gap-1 text-sm mb-3"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <span>뒤로</span>
            </TextButton>
            {/* 세그먼티드 토글(현재 모드 표시) — Button 갈래가 아니라 그대로 둔다.
                활성 칸을 disabled로 표시하는데 Button은 disabled에 opacity-50을 걸어 흐려진다. */}
            {/* eslint-disable local/no-self-painted-button */}
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                className="flex-1 px-3 py-2 text-sm font-medium text-white bg-brand-600 rounded-control"
                disabled
              >
                조직 멤버 추가
              </button>
              <button
                type="button"
                onClick={() => { setAssignMode('inviteExternal'); setAddMemberId(''); }}
                className="flex-1 px-3 py-2 text-sm font-medium text-ink-600 border border-ink-300 rounded-control hover:bg-ink-50"
              >
                외부 사용자 초대
              </button>
            </div>
            {/* eslint-enable local/no-self-painted-button */}
            <p className="text-sm text-ink-600 mb-2">
              조직 멤버 중 이 현장에 미배정된 멤버:
            </p>
            {unassignedMembers.length === 0 ? (
              <p className="text-sm text-ink-400 mb-2">추가 가능한 조직 멤버가 없습니다.</p>
            ) : (
              <div className="mb-3">
                <MemberSelect
                  members={unassignedMembers}
                  value={addMemberId}
                  onChange={setAddMemberId}
                  placeholder="멤버를 선택하세요"
                  searchable={unassignedMembers.length > 5}
                  disabled={isAddingMember}
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddMemberToSite}
                disabled={isAddingMember || !addMemberId}
              >
                {isAddingMember ? '추가 중...' : '추가'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="border border-ink-200 rounded-card p-3">
            <TextButton
              tone="neutral"
              onClick={() => setAssignMode('addMember')}
              className="flex items-center gap-1 text-sm mb-3"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <span>뒤로</span>
            </TextButton>
            {/* 세그먼티드 토글 — 위 addMember 모드와 같은 이유로 유지. */}
            {/* eslint-disable local/no-self-painted-button */}
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => { setAssignMode('addMember'); setInviteIdentifier(''); setInvitePhone(''); }}
                className="flex-1 px-3 py-2 text-sm font-medium text-ink-600 border border-ink-300 rounded-control hover:bg-ink-50"
              >
                조직 멤버 추가
              </button>
              <button
                type="button"
                className="flex-1 px-3 py-2 text-sm font-medium text-white bg-brand-600 rounded-control"
                disabled
              >
                외부 사용자 초대
              </button>
            </div>
            {/* eslint-enable local/no-self-painted-button */}
            <p className="text-sm text-ink-500 mb-3">
              새 멤버를 초대하면 수락 시 자동으로 이 현장에 배정됩니다.
            </p>
            <div className="mb-3">
              <label className="block text-xs font-medium text-ink-600 mb-1">
                이메일 또는 아이디
              </label>
              <input
                type="text"
                value={inviteIdentifier}
                onChange={(e) => setInviteIdentifier(e.target.value)}
                placeholder="user@example.com 또는 user123"
                disabled={isInviting}
                className="w-full px-3 py-2 text-sm border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
              />
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-ink-600 mb-1">
                전화번호 <span className="text-ink-400 font-normal">(선택)</span>
              </label>
              <input
                type="tel"
                inputMode="numeric"
                value={invitePhone}
                onChange={(e) =>
                  setInvitePhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 11))
                }
                placeholder="01012345678"
                maxLength={11}
                disabled={isInviting}
                className="w-full px-3 py-2 text-sm border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setAssignMode('list');
                  setInviteIdentifier('');
                  setInvitePhone('');
                }}
                disabled={isInviting}
              >
                취소
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleInviteExternal}
                disabled={isInviting || (!inviteIdentifier.trim() && !invitePhone.trim())}
              >
                {isInviting ? '초대 중...' : '초대'}
              </Button>
            </div>
          </div>
        )}
        {validationError && checkedCount > 0 && (
          <p className="mt-2 text-xs text-red-600">{validationError}</p>
        )}
      </div>
    </Modal>
  );
}
